<template>
  <controller :lockScroll="true" :goToTopButton="false">
    <section class="jarvis-embed-page">
      <div v-if="loading || error" :class="['jarvis-state', { error }]">
        <span v-if="loading" class="loader"></span>
        <p>{{ message }}</p>
        <button v-if="error" type="button" @click="startHandoff()">
          Tentar novamente
        </button>
      </div>
      <iframe
        v-else
        ref="jarvisFrame"
        :src="redirectUrl"
        title="Jarvis — Gatilhos de Intervenção"
        referrerpolicy="no-referrer"
        allow="clipboard-read; clipboard-write"
      ></iframe>
    </section>
  </controller>
</template>

<script>
import JarvisService from '@/services/jarvis.service'

export default {
  name: 'JarvisEmbed',
  data () {
    return {
      error: null,
      loading: true,
      message: 'Abrindo Jarvis...',
      redirectUrl: null,
      embedOrigin: null,
      routeSyncedFromEmbed: null
    }
  },
  watch: {
    '$route.fullPath' (fullPath) {
      if (this.routeSyncedFromEmbed === fullPath) {
        this.routeSyncedFromEmbed = null
        return
      }

      this.startHandoff()
    }
  },
  mounted () {
    window.addEventListener('message', this.onEmbedMessage)
    this.startHandoff()
  },
  beforeUnmount () {
    window.removeEventListener('message', this.onEmbedMessage)
  },
  methods: {
    startHandoff () {
      this.error = null
      this.loading = true
      this.message = 'Abrindo Jarvis...'
      this.redirectUrl = null
      this.embedOrigin = null

      JarvisService.redirectUrl({ redirectTo: this.redirectTo() }).then(
        redirectUrl => {
          try {
            const url = new URL(redirectUrl)
            const localHost = ['localhost', '127.0.0.1'].includes(url.hostname)

            if (url.protocol !== 'https:' && !localHost) {
              throw new Error('A origem do Jarvis não é segura.')
            }

            this.embedOrigin = url.origin
            this.redirectUrl = redirectUrl
            this.loading = false
          } catch (error) {
            this.showHandoffError(error)
          }
        },
        error => this.showHandoffError(error)
      )
    },
    showHandoffError (error) {
      this.error = error
      this.loading = false
      this.message = error.response?.data?.message || error.message || 'Não foi possível abrir o Jarvis.'

      window.toaster && window.toaster.emit('open', {
        style: 'warning',
        message: this.message,
        floater: true
      })
    },
    redirectTo () {
      const path = Array.isArray(this.$route.params.pathMatch)
        ? this.$route.params.pathMatch.join('/')
        : this.$route.params.pathMatch
      const base = path ? `/gatilhos/${path}` : '/'
      const params = new URLSearchParams()

      Object.keys(this.$route.query || {}).forEach(key => {
        if (['email', 'ticket', 'embed'].includes(key)) return

        const values = Array.isArray(this.$route.query[key])
          ? this.$route.query[key]
          : [this.$route.query[key]]

        values.forEach(value => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value)
          }
        })
      })

      const query = params.toString()
      return `${base}${query ? `?${query}` : ''}${this.$route.hash || ''}`
    },
    onEmbedMessage (event) {
      if (event.source !== this.$refs.jarvisFrame?.contentWindow || event.origin !== this.embedOrigin) return

      const payload = event.data || {}
      if (payload.source !== 'jarvis') return

      if (payload.type === 'jarvis:navigation' && payload.href) {
        this.syncApolloRoute(payload.href)
      }

      if (payload.type === 'jarvis:session-expired') {
        this.startHandoff()
      }
    },
    syncApolloRoute (href) {
      const apolloHref = this.apolloHrefFromJarvis(href)
      if (!apolloHref || apolloHref === this.$route.fullPath) return

      this.routeSyncedFromEmbed = apolloHref
      this.$router.replace(apolloHref).catch(error => {
        this.routeSyncedFromEmbed = null
        if (error?.name !== 'NavigationDuplicated') {
          console.error('Falha ao sincronizar a rota do Jarvis.', error)
        }
      })
    },
    apolloHrefFromJarvis (href) {
      try {
        const url = new URL(href, this.embedOrigin)
        if (url.origin !== this.embedOrigin) return null

        if (url.pathname === '/') {
          return `/ferramentas/jarvis-v2${url.search}${url.hash}`
        }

        const route = url.pathname.match(/^\/gatilhos(?:\/(novo|\d+)(?:\/(editar))?)?$/)
        if (!route) return null

        const path = [route[1], route[2]].filter(Boolean).join('/')
        return `/ferramentas/jarvis-v2${path ? `/${path}` : ''}${url.search}${url.hash}`
      } catch {
        return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.jarvis-embed-page {
  background-color: $color-bg;
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  width: 100%;

  iframe {
    border: 0;
    display: block;
    flex: 1;
    height: 100%;
    min-height: 0;
    width: 100%;
  }
}

.jarvis-state {
  align-items: center;
  color: $color-text;
  display: flex;
  flex: 1;
  flex-direction: column;
  font-weight: bold;
  gap: $hmg_small;
  justify-content: center;
  padding: $mg;
  text-align: center;

  p {
    margin: 0;
  }

  button {
    background-color: $color-primary;
    border: 0;
    border-radius: 24px;
    color: $color-secondary;
    cursor: pointer;
    font-weight: bold;
    min-height: 36px;
    padding: 0 $hmg_mid;
  }

  &.error {
    color: $color-primary;
  }
}
</style>
